import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Book,
  ChevronDown,
  ChevronUp,
  Filter,
  Calendar,
  FileText,
  CheckCircle2,
  XCircle,
  Loader2,
  ArrowLeft,
  BarChart3
} from 'lucide-react';
import { accountingAPI } from '../services/api';

const GeneralJournalPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [entryTypes, setEntryTypes] = useState([]);
  const [expandedEntries, setExpandedEntries] = useState(new Set());

  // Filters
  const [filters, setFilters] = useState({
    is_posted: ''
  });

  // Fetch data
  useEffect(() => {
    fetchJournalEntries();
  }, [filters]);

  const fetchJournalEntries = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.is_posted !== '') params.is_posted = filters.is_posted === 'true';

      const response = await accountingAPI.getJournalEntries(params);
      const entriesList = response.data || [];

      // List API returns total_debits, total_credits, line_items, entry_number — use as-is (one request, no N+1)
      const entriesWithDetails = entriesList.map((entry) => {
        const total_debits = Number(entry.total_debits) || 0;
        const total_credits = Number(entry.total_credits) || 0;
        return {
          ...entry,
          entry_number: entry.entry_number || `JE-${entry.id}`,
          line_items: entry.line_items || [],
          total_debits,
          total_credits,
          is_balanced: entry.is_balanced ?? (Math.abs(total_debits - total_credits) < 0.01)
        };
      });

      setEntries(entriesWithDetails);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    } finally {
      setLoading(false);
    }
  };


  const toggleEntry = (entryId) => {
    const newExpanded = new Set(expandedEntries);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedEntries(newExpanded);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    const n = Number(amount);
    if (n !== n) return '$0.00'; // NaN
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(n);
  };

  const clearFilters = () => {
    setFilters({ is_posted: '' });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/accounting')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Accounting
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Book className="w-8 h-8" />
              General Journal
            </h1>
            <p className="text-gray-600 mt-1">
              Complete audit trail of all accounting transactions
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filter by Status</h2>
        </div>
        <div className="flex gap-2">
          {['', 'true', 'false'].map((value) => (
            <button
              key={value}
              onClick={() => setFilters({ is_posted: value })}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                filters.is_posted === value
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {value === '' ? 'All' : value === 'true' ? 'Posted' : 'Draft'}
            </button>
          ))}
        </div>
      </div>

      {/* Journal Entries Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Journal Entries</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-12">
            <Book className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No journal entries found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">

                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entry #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Debits
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Credits
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {entries.map((entry) => (
                  <React.Fragment key={entry.id}>
                    {/* Main Entry Row */}
                    <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => toggleEntry(entry.id)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {expandedEntries.has(entry.id) ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {entry.entry_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatDate(entry.entry_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {entry.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(entry.total_debits)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(entry.total_credits)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {entry.is_balanced ? (
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            {entry.is_posted && (
                              <span className="text-xs text-green-600 font-medium">Posted</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <XCircle className="w-4 h-4 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">Unbalanced</span>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Expanded Line Items */}
                    {expandedEntries.has(entry.id) && (
                      <tr>
                        <td colSpan="7" className="px-6 py-4 bg-gray-50">
                          <div className="space-y-2">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Line Items:</h4>
                            <table className="w-full">
                              <thead className="bg-white border-b border-gray-200">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Line
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Account
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                    Description
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Debit
                                  </th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                    Credit
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-100">
                                {entry.line_items && entry.line_items.map((line) => (
                                  <tr key={line.id}>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {line.line_number}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-900">
                                      <div>
                                        <span className="font-medium">{line.account_number}</span>
                                        {' - '}
                                        <span>{line.account_name}</span>
                                      </div>
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-600">
                                      {line.description}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                                      {(Number(line.debit_amount) || 0) > 0 ? formatCurrency(line.debit_amount) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-right font-medium text-gray-900">
                                      {(Number(line.credit_amount) || 0) > 0 ? formatCurrency(line.credit_amount) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50">
                                <tr className="font-semibold">
                                  <td colSpan="3" className="px-4 py-2 text-sm text-right text-gray-700">
                                    Total:
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-900">
                                    {formatCurrency(entry.total_debits)}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-right text-gray-900">
                                    {formatCurrency(entry.total_credits)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralJournalPage;
